import React, { useState, useContext } from 'react';
import { connect } from 'react-redux';
import { Modal, Form, Button } from 'react-bootstrap';

import useForm from 'react-hook-form';
import { RHFInput } from 'react-hook-form-input';
import { isEmpty } from 'lodash';
import { SocketContext } from '../../../../../socketProvider';

import { closeBlockedSeatModal, addBlockedSeats } from '../actions';
import {
  ALL,
  expandBlockedSelection,
  getNewBlockedSeats,
  getSectionType,
} from '../utils';

import style from '../style.module.scss';

const defaultValues = {
  seatType: '',
  section: '',
  row: '',
  seat: '',
};

const BlockedSeatModal = ({
  event,
  blockedSeats,
  isBlockedSeatModalOpen,
  closeBlockedSeatModalAction,
  addBlockedSeatsAction,
}) => {
  const socket = useContext(SocketContext);
  const [sections, setSections] = useState([]);
  const [rows, setRows] = useState([]);
  const [seats, setSeats] = useState([]);
  const [isRepeatedSRS, setIsRepeatedSRS] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    getValues,
    reset,
  } = useForm(defaultValues);

  if (isEmpty(event) || !event.seatOptions) {
    return null;
  }

  const availableSeatTypes = Object.keys(event.seatOptions).filter((seatType) => {
    const { enabled, forcedDisable } = event.seatOptions[seatType];
    return enabled || forcedDisable;
  });

  if (!availableSeatTypes.length) {
    return null;
  }

  const resetForm = () => {
    reset(defaultValues);
    setSections([]);
    setRows([]);
    setSeats([]);
    setIsRepeatedSRS(false);
    setValidationError('');
    setIsSubmitting(false);
  };

  const closeHandler = () => {
    resetForm();
    closeBlockedSeatModalAction();
  };

  const submitHandler = async () => {
    setIsRepeatedSRS(false);
    setValidationError('');

    const values = getValues();
    const {
      seatType, section, row, seat,
    } = values;

    if (!seatType || !section || !row || !seat) {
      setValidationError('Please complete all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const expandedSeats = await expandBlockedSelection(socket, values);

      if (!expandedSeats.length) {
        setValidationError('No seats found for this selection.');
        return;
      }

      const newSeats = getNewBlockedSeats(blockedSeats, expandedSeats);

      if (!newSeats.length) {
        setIsRepeatedSRS(true);
        return;
      }

      addBlockedSeatsAction(newSeats);
      closeHandler();
    } catch (err) {
      console.error(err);
      setValidationError('Failed to load seats for this selection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSections = () => {
    const { seatType } = getValues();
    socket.emit('getSections', seatType, setSections);
  };

  const getRows = () => {
    const { seatType, section } = getValues();
    socket.emit('getRows', getSectionType(seatType), section, setRows);
  };

  const getSeats = () => {
    const { seatType, section, row } = getValues();

    if (row === ALL) {
      setSeats([]);
      setValue('seat', ALL);
      return;
    }

    socket.emit('getSeats', getSectionType(seatType), section, row, setSeats);
  };

  return (
    <Modal
      show={isBlockedSeatModalOpen}
      onHide={closeHandler}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title as="h3" className="w-100 text-center">Add blocked area</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="seatTypeInput">
          <Form.Label>Seat Type</Form.Label>
          <RHFInput
            name="seatType"
            as={(
              <Form.Control as="select" size="lg" custom>
                <option value="" disabled>Select a seat type</option>
                {availableSeatTypes.map((item) => (<option key={item}>{item}</option>))}
              </Form.Control>
            )}
            register={register({ required: true })}
            setValue={setValue}
            onChange={getSections}
          />
          {errors.seatType && (
            <span className={style.inputError}>Please, select a seat type</span>
          )}
        </Form.Group>

        <Form.Group controlId="sectionInput">
          <Form.Label>Section</Form.Label>
          <RHFInput
            name="section"
            as={(
              <Form.Control as="select" size="lg" custom>
                <option value="" disabled>Select a section</option>
                {sections.map((item) => (<option key={item}>{item}</option>))}
              </Form.Control>
            )}
            register={register({ required: true })}
            setValue={setValue}
            onChange={getRows}
          />
          {errors.section && (
            <span className={style.inputError}>Please, select a section</span>
          )}
        </Form.Group>

        <Form.Group controlId="rowInput">
          <Form.Label>Row</Form.Label>
          <RHFInput
            name="row"
            as={(
              <Form.Control as="select" size="lg" custom>
                <option value="" disabled>Select a row</option>
                <option value={ALL}>All rows</option>
                {rows.map((item) => (<option key={item}>{item}</option>))}
              </Form.Control>
            )}
            register={register({ required: true })}
            setValue={setValue}
            onChange={getSeats}
          />
        </Form.Group>

        <Form.Group className="mb-0" controlId="seatInput">
          <Form.Label>Seat</Form.Label>
          <RHFInput
            name="seat"
            as={(
              <Form.Control as="select" size="lg" custom>
                <option value="" disabled>Select a seat</option>
                <option value={ALL}>All seats</option>
                {seats.map((item) => (<option key={item}>{item}</option>))}
              </Form.Control>
            )}
            register={register({ required: true })}
            setValue={setValue}
          />
        </Form.Group>

        {validationError && (
          <span className={style.inputError}>{validationError}</span>
        )}
        {isRepeatedSRS && (
          <span className={style.inputError}>
            All seats in this selection are already blocked
          </span>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="small" variant="outline-primary" onClick={closeHandler} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          className="small"
          variant="primary"
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(submitHandler)}
        >
          {isSubmitting ? 'Loading seats...' : 'Add'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default connect(
  (
    { events: { selectedEvents }, blockedSeats: { isBlockedSeatModalOpen, blockedSeats } },
  ) => (
    { isBlockedSeatModalOpen, blockedSeats, event: selectedEvents[0] }
  ),
  { closeBlockedSeatModalAction: closeBlockedSeatModal, addBlockedSeatsAction: addBlockedSeats },
)(BlockedSeatModal);
